/** @velyn-custom BEGIN — vely-feature-cards.js
 *  Mobile carousel for Vely Feature Cards — desktop flex grid, optional mobile Swiper + pill pagination.
 */
(function () {
  const MOBILE_MAX = 767.98;

  function debounce(fn, wait) {
    let timer;
    return function debounced(...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  class VelyFeatureCards extends HTMLElement {
    connectedCallback() {
      if (this._velyFcInitStarted) return;
      this._velyFcInitStarted = true;

      this.enableCarousel = this.dataset.enableCarouselMobile === "true";
      this.itemsPerRow = parseFloat(this.dataset.itemsPerRow) || 1;
      this.spacing = parseFloat(this.dataset.spacing) || 24;
      this.track = this.querySelector(".vely-fc__grid");
      this.paginationEl = this.querySelector(".vely-swiper-pagination-pill");
      this._savedWidth = window.innerWidth;
      this._originalHtml = this.track?.innerHTML || "";

      this._onSectionLoad = (event) => {
        if (event.detail?.sectionId !== this.dataset.sectionId) return;
        this._destroySwiper(false);
        this._originalHtml = this.track?.innerHTML || "";
        this.waitForSwiper(() => this._syncLayout());
      };
      document.addEventListener("shopify:section:load", this._onSectionLoad);

      this._debouncedResize = debounce(() => this._onResize(), 250);
      window.addEventListener("resize", this._debouncedResize, { passive: true });

      this.waitForSwiper(() => this._syncLayout());
    }

    disconnectedCallback() {
      document.removeEventListener("shopify:section:load", this._onSectionLoad);
      window.removeEventListener("resize", this._debouncedResize);
      this._destroySwiper(true);
      this._velyFcInitStarted = false;
      this.classList.remove("vely-fc--ready");
    }

    waitForSwiper(callback, attempt = 0) {
      const hasSwiper = typeof window.Swiper === "function";
      const needsPagination = this.enableCarousel && this.paginationEl;
      const hasPaginationApi = !needsPagination || Boolean(window.VelySwiperPagination?.refresh);

      if (hasSwiper && hasPaginationApi) {
        callback();
        return;
      }
      if (attempt > 160) {
        console.warn("[vely-feature-cards] Swiper or VelySwiperPagination not available");
        return;
      }
      setTimeout(() => this.waitForSwiper(callback, attempt + 1), 50);
    }

    _isMobile() {
      return window.innerWidth <= MOBILE_MAX;
    }

    _onResize() {
      const width = window.innerWidth;
      const crossed =
        (width <= MOBILE_MAX && this._savedWidth > MOBILE_MAX) ||
        (width > MOBILE_MAX && this._savedWidth <= MOBILE_MAX);
      this._savedWidth = width;
      if (crossed || (this._isMobile() && this.enableCarousel)) {
        this._syncLayout();
      }
    }

    _syncLayout() {
      if (!this.track) return;

      if (this._isMobile() && this.enableCarousel) {
        this._initSwiper();
      } else {
        this._destroySwiper(true);
      }
    }

    refreshPagination() {
      if (!this._swiper || !this.paginationEl) return;
      window.VelySwiperPagination?.refresh?.(this._swiper, this.paginationEl);
    }

    _buildSlideHtml() {
      const temp = document.createElement("div");
      temp.innerHTML = this._originalHtml;
      temp.querySelectorAll(".vely-fc__card").forEach((el) => {
        el.classList.remove("switch-slide__mobile");
        el.classList.add("swiper-slide");
      });
      return temp.innerHTML;
    }

    _initSwiper() {
      const SwiperCtor = window.Swiper;
      if (typeof SwiperCtor !== "function" || !this.track || !this.enableCarousel) return;
      if (!this._isMobile()) return;

      if (this._swiper) {
        this._swiper.destroy(true, true);
        this._swiper = null;
      }

      const slideHtml = this._buildSlideHtml();
      this.track.classList.add("swiper");
      this.track.innerHTML = `<div class="swiper-wrapper">${slideHtml}</div>`;

      try {
        this._swiper = new SwiperCtor(this.track, {
          slidesPerView: this.itemsPerRow,
          spaceBetween: this.spacing,
          grabCursor: true,
          watchSlidesProgress: true,
          observer: true,
          observeParents: true,
          on: {
            slideChange: (swiper) => {
              if (!this.paginationEl) return;
              window.VelySwiperPagination?.setActiveBullet?.(swiper, this.paginationEl);
            },
            resize: () => this.refreshPagination(),
          },
        });
      } catch (error) {
        this._swiper = null;
        console.warn("[vely-feature-cards] Swiper init failed", error);
        return;
      }

      this.refreshPagination();
      requestAnimationFrame(() => this.refreshPagination());
      this.classList.add("vely-fc--ready");
    }

    _destroySwiper(restoreDom) {
      if (this._swiper) {
        this._swiper.destroy(true, true);
        this._swiper = null;
      }

      if (!restoreDom || !this.track) return;

      this.track.classList.remove(
        "swiper",
        "swiper-initialized",
        "swiper-horizontal",
        "swiper-backface-hidden"
      );
      this.track.innerHTML = this._originalHtml;
      this.classList.remove("vely-fc--ready");
    }
  }

  if (!customElements.get("vely-feature-cards")) {
    customElements.define("vely-feature-cards", VelyFeatureCards);
  }
})();
/** @velyn-custom END — vely-feature-cards.js */
